/**
 * Protected Route Component with RBAC
 * Guards routes based on authentication and permissions
 * 
 * Features:
 * - Redirects unauthenticated users to login
 * - Checks route-level permissions
 * - Shows access denied for unauthorized access
 * - Supports custom fallback components
 */
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { ShieldX, Home, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';

/**
 * Access Denied Component
 */
function AccessDenied({ requiredPermissions }) {
  const { roleDisplayName, homeRoute } = useAuth();
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <ShieldX className="h-8 w-8 text-red-600" />
          </div>
          <CardTitle className="text-xl text-gray-900">Access Denied</CardTitle>
          <CardDescription className="text-gray-600">
            You don't have permission to access this page
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-500 mb-1">Your current role:</div>
            <div className="font-medium text-gray-900">{roleDisplayName}</div>
          </div>
          
          <p className="text-sm text-gray-500 text-center">
            If you believe you should have access, please contact your administrator.
          </p>
          
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => window.history.back()}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go Back
            </Button>
            <Button
              className="flex-1"
              onClick={() => window.location.href = homeRoute}
            >
              <Home className="h-4 w-4 mr-2" />
              Go Home
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Loading Spinner Component
 */
function LoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
        <p className="mt-4 text-gray-500">Loading...</p>
      </div>
    </div>
  );
}

/**
 * Protected Route - requires authentication
 */
export function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  return children;
}

/**
 * Permission Protected Route - requires specific permissions
 */
export function PermissionRoute({ 
  children, 
  permissions = [], 
  requireAll = false,
  fallback = null 
}) {
  const { isAuthenticated, loading, checkPermission, checkAnyPermission } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  // Check permissions
  const hasAccess = permissions.length === 0 ? true : 
    requireAll 
      ? permissions.every(p => checkPermission(p))
      : checkAnyPermission(permissions);
  
  if (!hasAccess) {
    return fallback || <AccessDenied requiredPermissions={permissions} />;
  }
  
  return children;
}

/**
 * Route Protected Route - uses route path to determine permissions
 */
export function RouteProtectedRoute({ children, path }) {
  const { isAuthenticated, loading, checkRouteAccess } = useAuth();
  const location = useLocation();
  const routePath = path || location.pathname;
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  if (!checkRouteAccess(routePath)) {
    return <AccessDenied />;
  }
  
  return children;
}

/**
 * Higher-Order Component version
 */
export function withPermission(WrappedComponent, permissions = [], requireAll = false) {
  return function PermissionHOC(props) {
    return (
      <PermissionRoute permissions={permissions} requireAll={requireAll}>
        <WrappedComponent {...props} />
      </PermissionRoute>
    );
  };
}

/**
 * Hook for conditional rendering based on permissions
 */
export function usePermissionCheck(permissions, requireAll = false) {
  const { checkPermission, checkAnyPermission } = useAuth();
  
  if (!permissions || permissions.length === 0) return true;
  
  return requireAll 
    ? permissions.every(p => checkPermission(p))
    : checkAnyPermission(permissions);
}

export default ProtectedRoute;
